import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  mergeAgendaItems,
  getAgendaStatusBadge,
  getAgendaItemDate,
  getOrganismeLabel,
} from '../utils/agendaHelpers';
import AgendaReunionLogo from '../components/AgendaReunionLogo';
import Portal from '../components/Portal';
import {
  DocumentTextIcon,
  CalendarIcon,
  CalendarDaysIcon,
  NewspaperIcon,
  UserCircleIcon,
  HandRaisedIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  QuestionMarkCircleIcon,
  XMarkIcon,
  BanknotesIcon,
  BellAlertIcon,
} from '@heroicons/react/24/outline';

const ORGANISME_ORDER = ['SAR', 'AMAIR', 'ADAR'];

const GROUPEMENTS = {
  SAR: { nom: 'Syndicat Apicole de la Réunion', logo: '/logos/logo_sar.png', color: '#ffc34a' },
  AMAIR: { nom: "Association de la Maison de l'Apiculture de l'Île de la Réunion", logo: '/logos/logo_amair.png', color: '#70e65e' },
  ADAR: { nom: "Association pour le Développement de l'Apiculture à l'île de la Réunion", logo: '/logos/logo_adar.png', color: '#0B4395' },
};

const SERVICE_LABELS = {
  miellerie: 'Miellerie',
  assurance_unaf: "Services de l'UNAF",
};

const ADHESION_STATUS = {
  en_cours: { label: 'En cours', color: 'bg-yellow-100 text-yellow-800', icon: ClockIcon },
  paiement_demande: { label: 'Paiement demandé', color: 'bg-yellow-100 text-yellow-800', icon: ClockIcon },
  en_attente: { label: 'En attente', color: 'bg-honey-100 text-honey-800', icon: ClockIcon },
  actif: { label: 'Active', color: 'bg-green-100 text-green-800', icon: CheckCircleIcon },
  refuse: { label: 'Refusée', color: 'bg-red-100 text-red-800', icon: XCircleIcon },
  expiree: { label: 'Expirée', color: 'bg-gray-100 text-gray-800', icon: XCircleIcon },
};

const SERVICE_STATUS = {
  en_attente_paiement: { label: 'Paiement en attente', color: 'bg-honey-100 text-honey-800', icon: ClockIcon },
  en_attente_caution: {
    label: 'Caution en attente',
    color: 'bg-red-100 text-red-800',
    icon: ExclamationTriangleIcon,
    hint: "Veuillez remettre votre chèque de caution de 300€ au responsable lors de votre première visite. À défaut, l'accès à la miellerie ne pourra pas vous être accordé.",
  },
  actif: { label: 'Actif', color: 'bg-green-100 text-green-800', icon: CheckCircleIcon },
  en_attente_validation: { label: 'Validation en attente', color: 'bg-honey-100 text-honey-800', icon: ClockIcon },
  expire: { label: 'Expiré', color: 'bg-gray-100 text-gray-800', icon: XCircleIcon },
};

function StatusBadge({ status, kind = 'adhesion' }) {
  const [hintOpen, setHintOpen] = useState(false);
  const configMap = kind === 'service' ? SERVICE_STATUS : ADHESION_STATUS;
  const config = configMap[status] || {
    label: status,
    color: 'bg-slate-100 text-slate-800',
    icon: DocumentTextIcon,
  };
  const Icon = config.icon;
  const badgeClass = `inline-flex items-center pr-3.5 pl-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${config.color}`;

  if (!config.hint) {
    return (
      <span className={badgeClass}>
        <Icon className="w-3.5 h-3.5 mr-1 flex-shrink-0" />
        {config.label}
      </span>
    );
  }

  return (
    <span className="relative inline-flex">
      <button
        type="button"
        onClick={() => setHintOpen((open) => !open)}
        className={`${badgeClass} cursor-pointer hover:brightness-95`}
        aria-expanded={hintOpen}
      >
        <Icon className="w-3.5 h-3.5 mr-1 flex-shrink-0" />
        {config.label}
      </button>
      {hintOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setHintOpen(false)} />
          <div
            role="tooltip"
            className="absolute z-20 right-0 top-full mt-1.5 w-64 rounded-lg bg-white px-3 py-2 text-xs leading-snug text-gray-700 shadow-lg border border-gray-200"
          >
            {config.hint}
          </div>
        </>
      )}
    </span>
  );
}

function htmlToPlainText(html) {
  if (!html || typeof html !== 'string') return '';
  if (typeof document === 'undefined') {
    return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  }
  const el = document.createElement('div');
  el.innerHTML = html;
  return (el.textContent || el.innerText || '').replace(/\s+/g, ' ').trim();
}

/** Prochaine fin de miellée (dette à régler à cette date incluse). */
function prochaineEcheanceMiellee(now = new Date()) {
  const fins = [
    { month: 3, day: 30 },
    { month: 6, day: 30 },
    { month: 9, day: 31 },
    { month: 11, day: 20 },
  ];
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  for (const { month, day } of fins) {
    const candidate = new Date(now.getFullYear(), month, day);
    if (candidate >= today) return candidate;
  }
  return new Date(now.getFullYear() + 1, 3, 30);
}

function formatEcheanceFr(d) {
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}

function buildDashboardAlertes(adhesions, services, detteMiellerie, year, miellerieActif) {
  const alerts = [];

  adhesions.forEach((a) => {
    if (a.status === 'paiement_demande') {
      alerts.push({
        id: `pay-adh-${a._id}`,
        text: `Votre adhésion ${a.organisme} ${a.annee} est en attente de paiement.`,
        to: `/adhesion/${a._id}`,
        cta: 'Payer',
      });
    }
  });

  services.forEach((s) => {
    if (s.typeService === 'miellerie' && !miellerieActif) return;
    const label = SERVICE_LABELS[s.typeService] || s.nom || 'service';
    if (s.status === 'en_attente_paiement') {
      alerts.push({
        id: `pay-svc-${s._id}`,
        text: `Le paiement de votre ${label.toLowerCase()} (${s.organisme} ${s.annee}) est en attente.`,
        to: `/service/${s._id}`,
        cta: 'Payer',
      });
    }
    if (s.status === 'en_attente_caution') {
      alerts.push({
        id: `caution-${s._id}`,
        text: `Votre caution ${label.toLowerCase()} (${s.organisme} ${s.annee}) est en attente.`,
        to: `/service/${s._id}`,
        cta: 'Voir',
      });
    }
  });

  const miellerieEnCours = services.some(
    (s) =>
      s.typeService === 'miellerie' &&
      Number(s.annee) === year &&
      s.status !== 'abandonnee' &&
      s.status !== 'expire'
  );

  if (miellerieActif && detteMiellerie > 0 && !miellerieEnCours) {
    alerts.push({
      id: 'souscrire-miellerie',
      text: "Vous avez une dette miellerie sans adhésion miellerie en cours. Souscrivez pour régulariser votre situation.",
      to: '/service/miellerie',
      cta: 'Souscrire',
    });
  }

  return alerts;
}

function adhesionServiceSummary(nAdhesions, nServices, year) {
  const adhesionsLabel = `${nAdhesions} ${nAdhesions > 1 ? 'adhésions' : 'adhésion'}`;
  const servicesLabel = `${nServices} ${nServices > 1 ? 'services' : 'service'}`;
  const actifs = nAdhesions + nServices > 1 ? 'actifs' : 'actif';

  if (nAdhesions > 0 && nServices > 0) {
    return `Vous avez ${adhesionsLabel} et ${servicesLabel} ${actifs} en ${year}.`;
  }
  if (nAdhesions > 0) {
    return `Vous avez ${adhesionsLabel} ${nAdhesions > 1 ? 'actives' : 'active'} en ${year}.`;
  }
  if (nServices > 0) {
    return `Vous avez ${servicesLabel} ${actifs} en ${year}.`;
  }
  return `Vous n'avez pas d'adhésion active en ${year}.`;
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [adhesions, setAdhesions] = useState([]);
  const [services, setServices] = useState([]);
  const [actualites, setActualites] = useState([]);
  const [events, setEvents] = useState([]);
  const [reunions, setReunions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [detteMiellerie, setDetteMiellerie] = useState(0);
  const [miellerieActif, setMiellerieActif] = useState(false);
  const [showDetteHelp, setShowDetteHelp] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchDashboardData();
  }, [user, navigate]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const api = process.env.REACT_APP_API_URL;

      const adhesionsResponse = await axios.get(`${api}/adhesions/my-adhesions`, { headers });
      setAdhesions(
        Array.isArray(adhesionsResponse.data)
          ? adhesionsResponse.data
          : adhesionsResponse.data.adhesions || []
      );

      try {
        const servicesResponse = await axios.get(`${api}/services/my-services`, { headers });
        setServices(servicesResponse.data || []);
      } catch (err) {
        console.error('Erreur lors du chargement des services:', err);
      }

      try {
        const actualitesResponse = await axios.get(`${api}/actualites/articles?limit=4`, { headers });
        setActualites(actualitesResponse.data.articles || []);
      } catch (err) {
        console.error('Erreur lors du chargement des actualités:', err);
      }

      let miellerieOuverte = false;
      try {
        const settingsResponse = await axios.get(`${api}/service-settings`);
        miellerieOuverte = settingsResponse.data?.miellerieActif === true;
      } catch (err) {
        console.error('Erreur lors du chargement des paramètres services:', err);
      }
      setMiellerieActif(miellerieOuverte);

      if (miellerieOuverte) {
        try {
          const balanceResponse = await axios.get(`${api}/miellerie/balance?organisme=AMAIR`, { headers });
          const solde = Number(balanceResponse.data?.balance?.solde) || 0;
          setDetteMiellerie(solde < 0 ? Math.abs(solde) : 0);
        } catch {
          setDetteMiellerie(0);
        }
      } else {
        setDetteMiellerie(0);
      }

      try {
        const [eventsResponse, reunionsResponse] = await Promise.all([
          axios.get(`${api}/events/public?limit=5`, { headers }),
          axios.get(`${api}/reunions/mine`, { headers }),
        ]);
        setEvents(eventsResponse.data || []);
        setReunions(reunionsResponse.data || []);
      } catch (err) {
        console.error("Erreur lors du chargement de l'agenda:", err);
      }
    } catch (err) {
      setError('Erreur lors du chargement des données');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-lg text-slate-600">Chargement...</div>
      </div>
    );
  }

  const currentYear = new Date().getFullYear();
  const visibleAdhesions = adhesions.filter((a) => a.status !== 'abandonnee');
  const visibleServices = services.filter((s) => s.status !== 'abandonnee');
  const years = [...new Set([
    ...visibleAdhesions.map((a) => a.annee),
    ...visibleServices.map((s) => s.annee),
  ])].sort((a, b) => b - a);
  const focusYear = years.includes(currentYear) ? currentYear : (years[0] || currentYear);

  const yearAdhesions = visibleAdhesions.filter((a) => a.annee === focusYear);
  const yearServices = visibleServices.filter((s) => s.annee === focusYear);
  const activeAdhesions = yearAdhesions.filter((a) => a.status === 'actif');
  const activeServices = yearServices.filter((s) => s.status === 'actif');

  const groupingRows = ORGANISME_ORDER.map((organisme) => {
    const adhesion = yearAdhesions.find((a) => a.organisme === organisme);
    const groupServices = yearServices.filter((s) => s.organisme === organisme);
    if (!adhesion && groupServices.length === 0) return null;
    return { organisme, meta: GROUPEMENTS[organisme], adhesion, services: groupServices };
  }).filter(Boolean);

  const agendaItems = mergeAgendaItems(events, reunions)
    .filter((item) => item.kind === 'reunion' || getAgendaItemDate(item) >= new Date(new Date().setHours(0, 0, 0, 0)))
    .slice(0, 5);

  const alertes = buildDashboardAlertes(
    visibleAdhesions,
    visibleServices,
    detteMiellerie,
    currentYear,
    miellerieActif
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900">Espace personnel</h1>
        <p className="mt-2 text-sm text-slate-600">
          Bienvenue {user?.prenom} {user?.nom}
        </p>
      </div>

      {error && (
        <div className="mb-6 rounded-lg bg-red-50 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {showDetteHelp && (
        <Portal>
          <div
            className="fixed inset-0 z-[55] flex items-center justify-center p-4 bg-black/50"
            onClick={() => setShowDetteHelp(false)}
          >
            <div
              className="bg-white rounded-xl shadow-xl max-w-md w-full"
              role="dialog"
              aria-labelledby="dette-miellee-title"
              aria-modal="true"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                  <QuestionMarkCircleIcon className="h-5 w-5 text-honey-600 shrink-0" />
                  <h3 id="dette-miellee-title" className="text-base font-semibold text-slate-900">
                    Dette miellerie
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => setShowDetteHelp(false)}
                  className="p-1 rounded-lg text-slate-400 hover:bg-slate-50 hover:text-slate-600"
                  aria-label="Fermer"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="px-5 py-4">
                <p className="text-sm text-slate-700 leading-relaxed">
                  Toute dette doit être réglée avant la fin de la miellée courante.
                  Un rappel vous sera envoyé par courriel. En cas de non-paiement,
                  l&apos;accès à la miellerie ne sera plus disponible et vos réservations
                  seront annulées.
                </p>
              </div>
              <div className="px-5 py-3 border-t border-slate-100 flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowDetteHelp(false)}
                  className="px-4 py-2 text-sm font-semibold text-white bg-honey-600 rounded-lg hover:bg-honey-700"
                >
                  J&apos;ai compris
                </button>
              </div>
            </div>
          </div>
        </Portal>
      )}

      {alertes.length > 0 && (
        <div className="mb-6 bg-white shadow-sm rounded-xl overflow-hidden border border-red-300">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center">
            <BellAlertIcon className="h-5 w-5 text-red-500 mr-2 shrink-0" />
            <h3 className="text-lg font-semibold text-slate-900">Alertes</h3>
            <span className="ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-bold">
              {alertes.length}
            </span>
          </div>
          <ul className="divide-y divide-slate-100">
            {alertes.map((alerte) => (
              <li key={alerte.id} className="px-6 py-4">
                <div className="flex items-center gap-2">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
                  <div className="min-w-0 w-full flex items-center justify-between gap-2">
                    <p className="text-sm text-slate-700">{alerte.text}</p>
                    <Link
                      to={alerte.to}
                      className="mt-3 inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg text-white bg-red-500 hover:bg-red-600"
                    >
                      {alerte.cta}
                    </Link>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          <div className="flex flex-col gap-6">
          <div className="bg-white shadow-sm rounded-xl overflow-visible">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between rounded-t-xl">
              <div className="flex items-center">
                <DocumentTextIcon className="h-5 w-5 text-honey-500 mr-2" />
                <h3 className="text-lg font-semibold text-slate-900">Mes adhésions</h3>
              </div>
              <Link
                to="/mes-adhesions"
                className="text-sm font-medium text-indigo-600 hover:text-indigo-800 transition-colors"
              >
                Voir tout →
              </Link>
            </div>

            {groupingRows.length > 0 ? (
              <div className="px-6 py-5">
                <p className="text-sm text-slate-700">
                  {adhesionServiceSummary(activeAdhesions.length, activeServices.length, focusYear)}
                </p>

                <ul className="mt-4 divide-y divide-slate-100">
                  {groupingRows.map((row) => (
                    <li key={row.organisme} className="py-3 first:pt-0 last:pb-0">
                      <div className="flex items-start gap-5">
                        <img src={row.meta.logo} alt="" className="w-14 w-auto object-contain flex-shrink-0 mt-0.5" />
                        <div className="min-w-0 flex-1">
                          <span className="text-base font-bold text-slate-900">{row.organisme}</span>
                          <ul className="mt-1.5 space-y-1.5">
                            {row.adhesion && (
                              <li className="flex items-center justify-between gap-2 border border-slate-300 border-dashed px-3 py-2 rounded-md relative">
                                <span className="text-sm text-slate-600">Adhésion</span>
                                <StatusBadge status={row.adhesion.status} />
                              </li>
                            )}
                            {row.services.map((service) => (
                              <li key={service._id} className="flex items-center justify-between gap-2 border border-slate-300 border-dashed px-3 py-2 rounded-md relative">
                                <span className="text-sm text-slate-600">
                                  {SERVICE_LABELS[service.typeService] || service.nom || service.typeService}
                                </span>
                                <StatusBadge status={service.status} kind="service" />
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="px-6 py-12 text-center">
                <DocumentTextIcon className="mx-auto h-10 w-10 text-slate-300" />
                <h3 className="mt-2 text-sm font-medium text-slate-900">Aucune adhésion</h3>
                <p className="mt-1 text-sm text-slate-500">
                  Adhérez à un groupement et souscrivez à ses services depuis Mes adhésions.
                </p>
                <div className="mt-4">
                  <Link
                    to="/mes-adhesions"
                    className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg text-white bg-honey-500 hover:bg-honey-600"
                  >
                    Mes adhésions
                  </Link>
                </div>
              </div>
            )}
          </div>

          {miellerieActif && (
          <div className="bg-white shadow-sm rounded-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center">
              <BanknotesIcon className="h-5 w-5 text-honey-500 mr-2 shrink-0" />
              <h3 className="text-lg font-semibold text-slate-900">Dette miellerie</h3>
              <button
                type="button"
                onClick={() => setShowDetteHelp(true)}
                className="ml-1.5 p-0.5 rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                aria-label="En savoir plus sur la dette miellerie"
              >
                <QuestionMarkCircleIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-5">
              {detteMiellerie > 0 ? (
                <>
                  <p className="text-3xl font-bold text-slate-900 tabular-nums tracking-tight">
                    {detteMiellerie.toFixed(2)}&nbsp;€
                  </p>
                  <p className="text-sm text-slate-600 mt-2">
                    À régler avant le{' '}
                    <span className="font-semibold text-slate-800">{formatEcheanceFr(prochaineEcheanceMiellee())}</span>.
                  </p>
                  <Link
                    to="/miellerie?tab=solde"
                    className="mt-4 inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg text-white bg-honey-500 hover:bg-honey-600"
                  >
                    Régler
                  </Link>
                </>
              ) : (
                <p className="text-sm text-slate-700">Vous n&apos;avez pas de dette miellerie.</p>
              )}
            </div>
          </div>
          )}
          </div>

          <div className="bg-white shadow-sm rounded-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center">
                <NewspaperIcon className="h-5 w-5 text-honey-500 mr-2" />
                <h3 className="text-lg font-semibold text-slate-900">Actualités</h3>
              </div>
              <Link to="/actualites" className="text-sm font-medium text-indigo-600 hover:text-indigo-800 transition-colors">
                Voir tout →
              </Link>
            </div>

            <div className="divide-y divide-slate-100">
              {actualites.length > 0 ? (
                actualites.slice(0, 4).map((article) => (
                  <Link
                    key={article._id}
                    to={`/actualites/${article.slug}`}
                    className="block px-6 py-4 hover:bg-slate-50 transition-colors"
                  >
                    <h4 className="text-sm font-medium text-slate-900 line-clamp-2">{article.titre}</h4>
                    {article.extrait && (
                      <p className="text-xs text-slate-500 mt-1 line-clamp-2">{article.extrait}</p>
                    )}
                    <div className="flex items-center mt-2 text-xs text-slate-400">
                      <CalendarIcon className="h-3.5 w-3.5 mr-1" />
                      {new Date(article.datePublication || article.createdAt).toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                      {article.organisme && (
                        <span className="ml-2 px-1.5 py-0.5 bg-slate-100 rounded text-slate-500">
                          {article.organisme}
                        </span>
                      )}
                    </div>
                  </Link>
                ))
              ) : (
                <div className="px-6 py-12 text-center">
                  <NewspaperIcon className="mx-auto h-10 w-10 text-slate-300" />
                  <h3 className="mt-2 text-sm font-medium text-slate-900">Aucune actualité</h3>
                  <p className="mt-1 text-sm text-slate-500">
                    Pas d&apos;actualités pour le moment
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white shadow-sm rounded-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center">
                <CalendarDaysIcon className="h-5 w-5 text-honey-500 mr-2" />
                <h3 className="text-lg font-semibold text-slate-900">Agenda</h3>
              </div>
              <Link to="/events" className="text-sm font-medium text-indigo-600 hover:text-indigo-800 transition-colors">
                Voir tout →
              </Link>
            </div>

            {(() => {
              const pendingTotal = events.reduce((sum, ev) => sum + (ev.pendingVolunteersCount || 0), 0);
              const eventsWithPending = events.filter(ev => ev.pendingVolunteersCount > 0);
              if (!pendingTotal) return null;
              return (
                <div className="mx-4 mt-3 mb-1 flex items-center gap-3 px-4 py-2.5 bg-yellow-50 border border-yellow-200 rounded-xl">
                  <span className="flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full bg-yellow-400 text-yellow-900 text-xs font-bold">
                    {pendingTotal}
                  </span>
                  <div className="flex-1 min-w-0 text-xs">
                    <p className="font-semibold text-yellow-900">
                      {pendingTotal === 1 ? '1 volontaire en attente' : `${pendingTotal} volontaires en attente`}
                    </p>
                    <p className="text-yellow-700 truncate">
                      {eventsWithPending.map(ev => ev.title).join(', ')}
                    </p>
                  </div>
                </div>
              );
            })()}

            <div className="divide-y divide-slate-100">
              {agendaItems.length > 0 ? (
                agendaItems.map((item) => {
                  const isReunion = item.kind === 'reunion';
                  const badge = getAgendaStatusBadge(item);
                  const hasPending = !isReunion && item.pendingVolunteersCount > 0;
                  const descPreview = !isReunion ? htmlToPlainText(item.description) : null;
                  const linkTo = isReunion ? `/reunions/${item._id}` : `/events/${item._id}`;

                  return (
                    <Link
                      key={`${item.kind}-${item._id}`}
                      to={linkTo}
                      className="flex items-stretch gap-3 px-4 py-4 hover:bg-slate-50 transition-colors cursor-pointer"
                    >
                      {isReunion ? (
                        <AgendaReunionLogo organisme={item.organisme} />
                      ) : item.coverImage?.url ? (
                        <div className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden">
                          <img src={item.coverImage.url} alt={item.title} className="w-full h-full object-cover" />
                        </div>
                      ) : (
                        <div className="flex-shrink-0 w-16 h-16 rounded-lg bg-slate-100 flex items-center justify-center">
                          <CalendarDaysIcon className="h-6 w-6 text-slate-300" />
                        </div>
                      )}

                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-slate-900 line-clamp-1">{item.title}</h4>
                        {isReunion && item.organisme && (
                          <p className="text-xs text-indigo-600 font-medium mt-0.5 line-clamp-1">
                            {getOrganismeLabel(item.organisme)}
                          </p>
                        )}
                        {descPreview && (
                          <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{descPreview}</p>
                        )}
                        {isReunion && item.lieu && (
                          <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{item.lieu}</p>
                        )}
                        <div className="mt-1.5 flex items-center flex-wrap gap-1.5">
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${badge.cls}`}>
                            {badge.label}
                          </span>
                          {isReunion && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              Réunion convoquée
                            </span>
                          )}
                          {!isReunion && item.referents && item.referents.some(ref => ref._id === user._id) && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              <UserCircleIcon className="h-3 w-3 mr-1" />Référent
                            </span>
                          )}
                          {!isReunion && item.volunteerStatus && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              <HandRaisedIcon className="h-3 w-3 mr-1" />Volontaire
                            </span>
                          )}
                          {hasPending && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-bold bg-yellow-400 text-yellow-900">
                              {item.pendingVolunteersCount} volontaire{item.pendingVolunteersCount > 1 ? 's' : ''} en attente
                            </span>
                          )}
                        </div>
                      </div>
                    </Link>
                  );
                })
              ) : (
                <div className="px-6 py-12 text-center">
                  <CalendarDaysIcon className="mx-auto h-10 w-10 text-slate-300" />
                  <h3 className="mt-2 text-sm font-medium text-slate-900">Agenda vide</h3>
                  <p className="mt-1 text-sm text-slate-500">
                    Pas d&apos;événements ni de réunions à venir
                  </p>
                </div>
              )}
            </div>
          </div>
      </div>
    </div>
  );
}

